import type { AccountUuid, AttachedDoc, Class, Doc, IntegrationKind, Ref, Space, Timestamp } from '@hcengineering/core'
import type { Asset, IntlString, Metadata, Plugin } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import type { IntegrationType } from '@hcengineering/setting'
import type { AnyComponent } from '@hcengineering/ui'

/**
 * @public
 */
export const docsId = 'dztek-docs' as Plugin

/**
 * @public
 */
export const docsIntegrationKind = 'dztek-docs' as IntegrationKind

/**
 * Collection name under which StorageLink documents attach to any Doc.
 * @public
 */
export const STORAGE_LINKS_COLLECTION = 'companyDocs'

/**
 * Area of the bucket an object belongs to.
 * @public
 */
export type StorageKind = 'live' | 'archive'

/**
 * One object of the company bucket, recorded by the docs service.
 * @public
 */
export interface StorageObject extends Doc {
  /** Object key inside the bucket. */
  key: string
  /** Last path segment of the key. */
  name: string
  bucket: string
  size: number
  contentType: string
  etag: string
  lastModified: Timestamp
  kind: StorageKind
  /** Class of the Huly object an archived item was copied from. */
  sourceClass?: Ref<Class<Doc>>
  /** Id of the Huly object an archived item was copied from. */
  sourceId?: Ref<Doc>
  archivedBy?: AccountUuid
  archivedAt?: Timestamp
}

/**
 * Link from any Huly object to a key of the company bucket.
 * @public
 */
export interface StorageLink extends AttachedDoc {
  key: string
  name: string
  object?: Ref<StorageObject>
}

export default plugin(docsId, {
  class: {
    StorageObject: '' as Ref<Class<StorageObject>>,
    StorageLink: '' as Ref<Class<StorageLink>>
  },
  space: {
    Storage: '' as Ref<Space>
  },
  app: {
    Docs: '' as Ref<Doc>
  },
  component: {
    Connect: '' as AnyComponent,
    Configure: '' as AnyComponent,
    ServiceStatus: '' as AnyComponent,
    IconDocs: '' as AnyComponent,
    Browse: '' as AnyComponent,
    Archive: '' as AnyComponent,
    StorageObjectPresenter: '' as AnyComponent,
    StorageLinkPresenter: '' as AnyComponent,
    SourcePresenter: '' as AnyComponent,
    StorageLinks: '' as AnyComponent,
    LinkDocPopup: '' as AnyComponent
  },
  integrationType: {
    Docs: '' as Ref<IntegrationType>
  },
  metadata: {
    ServiceUrl: '' as Metadata<string>
  },
  string: {
    Docs: '' as IntlString,
    Description: '' as IntlString,
    ObjectStorage: '' as IntlString,
    ObjectStorageDescription: '' as IntlString,
    Browse: '' as IntlString,
    Archive: '' as IntlString,
    StorageObject: '' as IntlString,
    StorageObjects: '' as IntlString,
    StorageLink: '' as IntlString,
    StorageLinks: '' as IntlString,
    Key: '' as IntlString,
    Name: '' as IntlString,
    Bucket: '' as IntlString,
    Size: '' as IntlString,
    ContentType: '' as IntlString,
    Etag: '' as IntlString,
    LastModified: '' as IntlString,
    Kind: '' as IntlString,
    KindLive: '' as IntlString,
    KindArchive: '' as IntlString,
    Source: '' as IntlString,
    SourceClass: '' as IntlString,
    ArchivedBy: '' as IntlString,
    ArchivedAt: '' as IntlString,
    CompanyDocs: '' as IntlString
  },
  icon: {
    Docs: '' as Asset,
    Browse: '' as Asset,
    Archive: '' as Asset,
    Folder: '' as Asset,
    File: '' as Asset,
    Upload: '' as Asset,
    Download: '' as Asset,
    Link: '' as Asset
  }
})
